import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Factory, Calendar, Package, AlertTriangle, Hash } from 'lucide-react';

interface Product {
  product_id: number;
  name: string;
  description?: string;
  price: string | number;
  status: 'Active' | 'Inactive' | 'Discontinued';
  created_at: string;
  updated_at: string;
}

interface Batch {
  batch_id: number;
  product_id: number;
  production_date: string;
  created_at: string;
  batch_number?: string;
  product?: Product;
  _count?: {
    complaints: number;
  };
}

interface BatchViewModalProps {
  batch: Batch | null;
  open: boolean;
  onClose: () => void;
}

export const BatchViewModal: React.FC<BatchViewModalProps> = ({
  batch,
  open,
  onClose,
}) => {
  if (!batch) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateOnly = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <Factory className="h-5 w-5 text-blue-600" />
            Batch Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardContent className="p-4">
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {batch.batch_number || `BATCH-${batch.batch_id.toString().padStart(4, '0')}`}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Batch ID: #{batch.batch_id}
                    </p>
                  </div>
                  <Badge className={`${(batch._count?.complaints || 0) > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                    {(batch._count?.complaints || 0) > 0 ? 'Has Complaints' : 'No Complaints'}
                  </Badge>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Product
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {batch.product?.name || 'Unknown Product'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-blue-50 dark:bg-blue-900/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-500 p-2 rounded-lg">
                    <Hash className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Batch Number</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {batch.batch_number || `BATCH-${batch.batch_id.toString().padStart(4, '0')}`}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-green-50 dark:bg-green-900/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-green-500 p-2 rounded-lg">
                    <Package className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Product</p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {batch.product?.name || 'Unknown'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-orange-50 dark:bg-orange-900/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-orange-500 p-2 rounded-lg">
                    <AlertTriangle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Complaints</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {batch._count?.complaints || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-purple-50 dark:bg-purple-900/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-500 p-2 rounded-lg">
                    <Calendar className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Production Date</p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatDateOnly(batch.production_date)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Product Details */}
          {batch.product && (
            <Card>
              <CardContent className="p-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Product Information
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Product Name:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {batch.product.name}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Price:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      ${parseFloat(batch.product.price.toString()).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
                    <Badge className={`${
                      batch.product.status === 'Active' ? 'bg-green-100 text-green-800' :
                      batch.product.status === 'Inactive' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {batch.product.status}
                    </Badge>
                  </div>
                  {batch.product.description && (
                    <div className="pt-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Description:</span>
                      <p className="text-sm text-gray-900 dark:text-white mt-1">
                        {batch.product.description}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Timestamps */}
          <Card>
            <CardContent className="p-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Timeline
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Production Date:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatDateOnly(batch.production_date)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Created:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatDate(batch.created_at)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};